/**
 * Project Detail Page - SUPABASE VERSION
 * ========================================
 * Shows project info and linked materials.
 * All data from Supabase via project_materials junction table.
 * Includes material similarity feature.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Box, Calendar, Sparkles, X } from 'lucide-react';

// Import API service
import { getProjectById, getProjectMaterials } from '../services/materialsApi';

// Import styles
import '../styles/dashboard.css';

// =============================================================================
// DESIGN-AWARE MATERIAL SIMILARITY ALGORITHM
// =============================================================================
// Scoring prioritizes interior design coherence over technical similarity.
// A white marble should suggest other light, elegant materials - not black marble.

// Color families for design coherence
const COLOR_FAMILIES = {
  LIGHT: ['white', 'beige', 'cream', 'ivory', 'light grey', 'light gray'],
  DARK: ['black', 'dark grey', 'dark gray', 'brown', 'nero', 'charcoal'],
  WARM: ['beige', 'gold', 'brown', 'honey', 'caramel', 'tan', 'amber', 'cream'],
  COOL: ['grey', 'gray', 'blue', 'silver', 'slate'],
  EARTH: ['green', 'brown', 'terracotta', 'rust'],
  VIBRANT: ['red', 'pink', 'coral', 'burgundy']
};

// Opposite color pairs (design clash)
const COLOR_OPPOSITES = {
  white: ['black', 'nero'],
  black: ['white', 'bianco'],
  beige: ['black', 'nero'],
  light: ['dark'],
  warm: ['cool']
};

// Premium material keywords (for name-based boost)
const PREMIUM_KEYWORDS = [
  'calacatta', 'statuario', 'arabescato', 'carrara', 'paonazzo',
  'taj mahal', 'azul', 'onyx', 'agata', 'tiger'
];

// Style classification keywords
const STYLE_KEYWORDS = {
  soft: [
    'calacatta', 'statuario', 'bianco', 'white', 'cream', 'subtle',
    'delicate', 'fine', 'perlino', 'botticino', 'crema'
  ],
  dramatic: [
    'nero', 'marquina', 'portoro', 'black', 'gold', 'contrast',
    'bold', 'striking', 'intense', 'fusion', 'tempest'
  ],
  uniform: [
    'absolute', 'pure', 'solid', 'thassos', 'sivec', 'uniform',
    'homogeneous', 'plain'
  ]
};

/**
 * Determine color family membership
 */
const getColorFamilies = (color) => {
  if (!color) return [];
  const c = color.toLowerCase();
  return Object.entries(COLOR_FAMILIES)
    .filter(([, colors]) => colors.some(fc => c.includes(fc) || fc.includes(c)))
    .map(([family]) => family);
};

/**
 * Check if two colors are opposites (design clash)
 */
const areColorsOpposite = (color1, color2) => {
  if (!color1 || !color2) return false;
  const c1 = color1.toLowerCase();
  const c2 = color2.toLowerCase();
  
  // Direct opposites
  if (COLOR_OPPOSITES[c1]?.some(opp => c2.includes(opp))) return true;
  if (COLOR_OPPOSITES[c2]?.some(opp => c1.includes(opp))) return true;
  
  // Family opposites (LIGHT vs DARK)
  const families1 = getColorFamilies(c1);
  const families2 = getColorFamilies(c2);
  
  if (families1.includes('LIGHT') && families2.includes('DARK')) return true;
  if (families1.includes('DARK') && families2.includes('LIGHT')) return true;
  
  return false;
};

/**
 * Derive style classification from material name and pattern
 */
const deriveStyle = (material) => {
  if (!material) return 'unknown';
  
  const searchText = [
    material.name || '',
    material.pattern || '',
    material.dominant_color || ''
  ].join(' ').toLowerCase();
  
  // Check each style
  for (const [style, keywords] of Object.entries(STYLE_KEYWORDS)) {
    if (keywords.some(kw => searchText.includes(kw))) {
      return style;
    }
  }
  
  // Default based on pattern
  const pattern = (material.pattern || '').toLowerCase();
  if (pattern.includes('vein') || pattern.includes('soft')) return 'soft';
  if (pattern.includes('dramatic') || pattern.includes('bold')) return 'dramatic';
  if (pattern.includes('uniform') || pattern.includes('solid')) return 'uniform';
  
  return 'neutral';
};

/**
 * Check if material name contains premium keywords
 */
const isPremiumMaterial = (name) => {
  if (!name) return false;
  const n = name.toLowerCase();
  return PREMIUM_KEYWORDS.some(kw => n.includes(kw));
};

/**
 * DESIGN-AWARE Similarity Score
 * 
 * Scoring system:
 * 1. Dominant Color (PRIMARY): +80 exact, +30 family, -40 opposite
 * 2. Category: +10 if same
 * 3. Pattern: +5 if match (reduced importance)
 * 4. Style: +25 same, -20 conflicting
 * 5. Premium boost: +15 if both premium
 */
const computeSimilarityScore = (material1, material2) => {
  if (!material1 || !material2 || material1.id === material2.id) return -Infinity;
  
  let score = 0;
  const breakdown = {}; // For debugging
  
  // ---------------------------------------------------------------------------
  // 1. DOMINANT COLOR (PRIMARY FACTOR - highest weight)
  // ---------------------------------------------------------------------------
  const color1 = (material1.dominant_color || '').toLowerCase();
  const color2 = (material2.dominant_color || '').toLowerCase();
  
  if (color1 && color2) {
    if (color1 === color2) {
      // Exact match
      score += 80;
      breakdown.color = '+80 (exact)';
    } else {
      // Check color families
      const families1 = getColorFamilies(color1);
      const families2 = getColorFamilies(color2);
      const sharedFamilies = families1.filter(f => families2.includes(f));
      
      if (sharedFamilies.length > 0) {
        // Same color family
        score += 30;
        breakdown.color = `+30 (family: ${sharedFamilies.join(', ')})`;
      } else if (areColorsOpposite(color1, color2)) {
        // Opposite colors - penalize heavily
        score -= 40;
        breakdown.color = '-40 (opposite)';
      } else {
        breakdown.color = '0 (different)';
      }
    }
  }
  
  // ---------------------------------------------------------------------------
  // 2. CATEGORY (low weight)
  // ---------------------------------------------------------------------------
  const cat1 = (material1.category || '').toLowerCase();
  const cat2 = (material2.category || '').toLowerCase();
  
  if (cat1 && cat2 && cat1 === cat2) {
    score += 10;
    breakdown.category = '+10';
  }
  
  // ---------------------------------------------------------------------------
  // 3. PATTERN (reduced importance)
  // ---------------------------------------------------------------------------
  const pattern1 = (material1.pattern || '').toLowerCase();
  const pattern2 = (material2.pattern || '').toLowerCase();
  
  if (pattern1 && pattern2 && pattern1 === pattern2) {
    score += 5;
    breakdown.pattern = '+5';
  }
  
  // ---------------------------------------------------------------------------
  // 4. STYLE CLASSIFICATION (design coherence)
  // ---------------------------------------------------------------------------
  const style1 = deriveStyle(material1);
  const style2 = deriveStyle(material2);
  
  if (style1 !== 'unknown' && style2 !== 'unknown' && style1 !== 'neutral' && style2 !== 'neutral') {
    if (style1 === style2) {
      // Same style
      score += 25;
      breakdown.style = `+25 (both ${style1})`;
    } else if (
      (style1 === 'soft' && style2 === 'dramatic') ||
      (style1 === 'dramatic' && style2 === 'soft')
    ) {
      // Conflicting styles
      score -= 20;
      breakdown.style = `-20 (${style1} vs ${style2})`;
    }
  }
  
  // ---------------------------------------------------------------------------
  // 5. PREMIUM MATERIAL BOOST
  // ---------------------------------------------------------------------------
  const premium1 = isPremiumMaterial(material1.name);
  const premium2 = isPremiumMaterial(material2.name);
  
  if (premium1 && premium2) {
    // Both premium - boost similarity
    score += 15;
    breakdown.premium = '+15 (both premium)';
  } else if (premium1 && !premium2) {
    // Premium paired with generic - slight penalty
    score -= 5;
    breakdown.premium = '-5 (premium vs generic)';
  }
  
  // Debug logging (can be removed in production)
  // console.log(`Similarity: ${material1.name} <-> ${material2.name}`, { score, breakdown });
  
  return score;
};

/**
 * Material Card Component (Clickable)
 */
const MaterialCard = ({ material, testId, onClick, isSelected }) => {
  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(material);
    }
  };

  return (
    <div 
      className="db-i-card"
      data-testid={testId}
      onClick={handleClick}
      style={{ 
        cursor: 'pointer',
        border: isSelected ? '2px solid var(--gold)' : '1px solid var(--border)',
        transform: isSelected ? 'scale(1.02)' : 'none',
        transition: 'all 0.2s ease'
      }}
    >
      <img 
        src={material.image_url || 'https://images.unsplash.com/photo-1558346648-9757f2fa4474?w=400&q=75'} 
        alt={material.name}
        loading="lazy"
        style={{ aspectRatio: '4/3', objectFit: 'cover', width: '100%' }}
      />
      <div className="db-i-card-lbl">
        <div style={{ fontWeight: 600, color: 'var(--t1)', marginBottom: 2 }}>
          {material.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>
          {material.category || 'Stone'} · {material.dominant_color || 'N/A'}
        </div>
        {material.notes && (
          <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 4, fontStyle: 'italic' }}>
            {material.notes}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Similar Material Card (with score badge)
 */
const SimilarMaterialCard = ({ material, score, onClick }) => {
  return (
    <div 
      className="db-i-card"
      onClick={() => onClick?.(material)}
      style={{ cursor: 'pointer', position: 'relative' }}
    >
      {/* Score badge */}
      <div style={{
        position: 'absolute',
        top: 8,
        right: 8,
        background: 'var(--gold)',
        color: '#000',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        zIndex: 1
      }}>
        {score}% match
      </div>
      <img 
        src={material.image_url || 'https://images.unsplash.com/photo-1558346648-9757f2fa4474?w=400&q=75'} 
        alt={material.name}
        loading="lazy"
        style={{ aspectRatio: '4/3', objectFit: 'cover', width: '100%' }}
      />
      <div className="db-i-card-lbl">
        <div style={{ fontWeight: 600, color: 'var(--t1)', marginBottom: 2 }}>
          {material.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t2)' }}>
          {material.category || 'Stone'} · {material.dominant_color || 'N/A'}
        </div>
      </div>
    </div>
  );
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // Compute similar materials when a material is selected
  const similarMaterials = useMemo(() => {
    if (!selectedMaterial || materials.length < 2) return [];
    
    console.log("🎨 Computing DESIGN-AWARE similarity for:", selectedMaterial.name);
    
    // Calculate scores for all other materials
    const scored = materials
      .filter(m => m && m.id !== selectedMaterial.id)
      .map(m => ({
        material: m,
        score: computeSimilarityScore(selectedMaterial, m)
      }))
      .filter(item => item.score > 20) // Minimum threshold for design coherence
      .sort((a, b) => b.score - a.score) // Sort by score descending
      .slice(0, 4); // Top 4
    
    console.log("🎨 Similar materials (design-aware):", scored.map(s => ({
      name: s.material.name,
      score: s.score,
      color: s.material.dominant_color
    })));
    
    // Normalize scores to percentage (max possible: 80+10+5+25+15 = 135)
    const maxScore = 135;
    return scored.map(item => ({
      ...item,
      scorePercent: Math.min(100, Math.max(0, Math.round((item.score / maxScore) * 100)))
    }));
  }, [selectedMaterial, materials]);

  // Handle material card click
  const handleMaterialClick = (material) => {
    console.log("🔥 Material clicked:", material.name);
    if (selectedMaterial?.id === material.id) {
      setSelectedMaterial(null); // Deselect if same
    } else {
      setSelectedMaterial(material);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedMaterial(null);
  };

  // Fetch project and materials from Supabase
  useEffect(() => {
    console.log('🚀 Loading project detail from Supabase');
    console.log('📍 Project ID:', projectId);
    
    const fetchProjectData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch project and materials
        const projectRes = await getProjectById(projectId);
        
        if (projectRes.success) {
          setProject(projectRes.data);
          console.log('✅ Project loaded:', projectRes.data?.name);
        } else {
          setError('Progetto non trovato');
          console.error('❌ Project not found');
        }
        
        // Materials now returns array directly
        const materialsData = await getProjectMaterials(projectId);
        console.log('🔥 Materials data:', materialsData);
        
        if (Array.isArray(materialsData)) {
          setMaterials(materialsData.filter(m => m !== null));
        }
      } catch (err) {
        console.error('❌ Error loading project:', err);
        setError('Errore nel caricamento del progetto');
      } finally {
        setLoading(false);
      }
    };
    
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-shell" data-testid="project-detail-loading">
        <div className="db-main" style={{ width: '100%' }}>
          <div className="db-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
            <div style={{ textAlign: 'center', color: 'var(--t2)' }}>
              <div style={{ fontSize: 18, marginBottom: 8 }}>Caricamento progetto...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="dashboard-shell" data-testid="project-detail-error">
        <div className="db-main" style={{ width: '100%' }}>
          <div className="db-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🏗️</div>
              <div style={{ fontSize: 18, color: 'var(--t1)', marginBottom: 8 }}>
                {error || 'Progetto non trovato'}
              </div>
              <button 
                onClick={() => navigate('/dashboard')}
                className="db-a-btn"
                style={{ marginTop: 16 }}
              >
                Torna alla Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell" data-testid="project-detail">
      <div className="db-main" style={{ width: '100%' }}>
        {/* Header */}
        <header className="db-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button 
              onClick={() => navigate(-1)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--t2)', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <ArrowLeft size={18} />
              <span>Indietro</span>
            </button>
            <span className="db-topbar-label">DETTAGLIO PROGETTO</span>
          </div>
        </header>

        {/* Content */}
        <main className="db-content">
          {/* Project Header */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 2fr', 
            gap: 24, 
            marginBottom: 32,
            background: 'var(--card)',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid var(--border)'
          }}>
            {/* Project Image */}
            <div style={{ aspectRatio: '4/3', overflow: 'hidden' }}>
              <img 
                src={project.image || 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=600&q=80'}
                alt={project.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            
            {/* Project Info */}
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h1 style={{ 
                fontFamily: 'var(--ff-head)', 
                fontSize: 32, 
                fontWeight: 600, 
                marginBottom: 12,
                color: 'var(--t1)'
              }}>
                {project.name}
              </h1>
              
              <div style={{ display: 'flex', gap: 24, color: 'var(--t2)', fontSize: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={16} />
                  <span>Aggiornato: {formatDate(project.updated_at)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Box size={16} />
                  <span>{materials.length} materiali</span>
                </div>
              </div>
              
              {project.notes && (
                <p style={{ marginTop: 16, color: 'var(--t2)', lineHeight: 1.6 }}>
                  {project.notes}
                </p>
              )}
            </div>
          </div>

          {/* Materials Section */}
          <div className="db-sec-hdr" data-testid="section-project-materials">
            <h2 className="db-sec-title">Materiali del Progetto</h2>
            <span style={{ fontSize: 13, color: 'var(--t2)' }}>
              {selectedMaterial 
                ? `Selezionato: ${selectedMaterial.name}` 
                : `${materials.length} materiali · Clicca per confrontare`}
            </span>
          </div>

          {materials.length > 0 ? (
            <div 
              className="db-insp-grid" 
              style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
              data-testid="project-materials-grid"
            >
              {materials.map((material, index) => (
                <MaterialCard
                  key={material.id || index}
                  material={material}
                  testId={`project-material-${index}`}
                  onClick={handleMaterialClick}
                  isSelected={selectedMaterial?.id === material.id}
                />
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: 40, 
              textAlign: 'center', 
              color: 'var(--t2)',
              background: 'var(--card)',
              borderRadius: 12,
              border: '1px solid var(--border)'
            }}>
              <Box size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
              <div style={{ fontSize: 16, marginBottom: 8 }}>
                Nessun materiale collegato a questo progetto
              </div>
              <Link to="/materials" className="db-a-btn" style={{ marginTop: 16, display: 'inline-block' }}>
                Esplora Materiali
              </Link>
            </div>
          )}

          {/* Similar Materials Section - Shows when a material is selected */}
          {selectedMaterial && (
            <div style={{ marginTop: 32 }}>
              <div className="db-sec-hdr" data-testid="section-similar-materials">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Sparkles size={20} style={{ color: 'var(--gold)' }} />
                  <h2 className="db-sec-title">Materiali Simili a "{selectedMaterial.name}"</h2>
                </div>
                <button 
                  onClick={clearSelection}
                  style={{ 
                    background: 'transparent', 
                    border: '1px solid var(--border)', 
                    borderRadius: 6,
                    padding: '6px 12px',
                    color: 'var(--t2)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12
                  }}
                >
                  <X size={14} />
                  Chiudi
                </button>
              </div>

              {similarMaterials.length > 0 ? (
                <div 
                  className="db-insp-grid" 
                  style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
                  data-testid="similar-materials-grid"
                >
                  {similarMaterials.map((item, index) => (
                    <SimilarMaterialCard
                      key={item.material.id || index}
                      material={item.material}
                      score={item.scorePercent}
                      onClick={handleMaterialClick}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: 24, 
                  textAlign: 'center', 
                  color: 'var(--t2)',
                  background: 'var(--card)',
                  borderRadius: 12,
                  border: '1px solid var(--border)'
                }}>
                  Nessun materiale simile trovato nel progetto
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
